import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import config from 'config/config';
import {
  loadGoogleAnalytics,
  loadAhrefsAnalytics,
  loadClarity,
} from 'Utilities/thirdPartyScripts';

const STORAGE_KEY = 'cookieConsent';

export type ConsentStatus = 'accepted' | 'rejected' | null;

interface StoredConsent {
  status: 'accepted' | 'rejected';
  timestamp: string;
}

interface CookieConsentContextValue {
  status: ConsentStatus;
  hydrated: boolean;
  analyticsGranted: boolean;
  acceptAll: () => void;
  rejectNonEssential: () => void;
  resetConsent: () => void;
}

const CookieConsentContext = createContext<
  CookieConsentContextValue | undefined
>(undefined);

const readStoredConsent = (): ConsentStatus => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    return parsed.status === 'accepted' || parsed.status === 'rejected'
      ? parsed.status
      : null;
  } catch {
    return null;
  }
};

const writeStoredConsent = (status: 'accepted' | 'rejected') => {
  try {
    const value: StoredConsent = { status, timestamp: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* do nothing */
  }
};

const clearStoredConsent = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* do nothing */
  }
};

interface CookieConsentProviderProps {
  children: ReactNode;
}

export const CookieConsentProvider = ({
  children,
}: CookieConsentProviderProps) => {
  const [status, setStatus] = useState<ConsentStatus>(null);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    setStatus(readStoredConsent());
    setHydrated(true);
  }, []);

  const analyticsGranted = status === 'accepted';

  useEffect(() => {
    if (!analyticsGranted) return;
    if (config.GA_ID) loadGoogleAnalytics(config.GA_ID);
    if (config.AHREFS_KEY) loadAhrefsAnalytics(config.AHREFS_KEY);
    if (config.CLARITY_ID) loadClarity(config.CLARITY_ID);
  }, [analyticsGranted]);

  const acceptAll = () => {
    writeStoredConsent('accepted');
    setStatus('accepted');
  };

  const rejectNonEssential = () => {
    writeStoredConsent('rejected');
    setStatus('rejected');
  };

  const resetConsent = () => {
    clearStoredConsent();
    setStatus(null);
  };

  return (
    <CookieConsentContext.Provider
      value={{
        status,
        hydrated,
        analyticsGranted,
        acceptAll,
        rejectNonEssential,
        resetConsent,
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = (): CookieConsentContextValue => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider',
    );
  }
  return context;
};
